/**
 * SmoothCursorScrollPlugin
 *
 * Overrides the browser's default jumpy scroll-into-view behavior for arrow key
 * navigation in contenteditable. Instead of jumping by a large chunk when the
 * cursor moves past the visible area, this plugin scrolls by exactly the amount
 * needed to keep the cursor visible plus a small margin — producing smooth,
 * line-by-line scrolling similar to Google Docs.
 *
 * How it works:
 * 1. On ArrowDown/ArrowUp keydown, save the scroll container's scrollTop.
 * 2. In a requestAnimationFrame callback (after the browser moves the cursor
 *    but before paint), calculate the cursor's position in the scroll container's
 *    coordinate space.
 * 3. Determine the minimum scroll needed to keep the cursor visible.
 * 4. Override scrollTop, replacing whatever the browser set.
 */

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { useEffect } from 'react'

import { inlineEmbedDOMCaretRect } from '../inlineEmbedCaret'

const MARGIN = 30 // breathing room kept above/below the cursor

function findScrollContainer(element: HTMLElement): HTMLElement | null {
  let current = element.parentElement
  while (current) {
    const style = getComputedStyle(current)
    const overflowY = style.overflowY
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return current
    }
    current = current.parentElement
  }
  return null
}

/**
 * Rect of the moving end of the selection.
 *
 * The focus point, not the range as a whole: with Shift held the range spans
 * everything selected so far, and its bottom stays pinned near where the
 * selection started — which reads as "still visible" and suppresses the scroll
 * the arrow key should have caused.
 */
function measureCaret(): DOMRect | null {
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0 || !sel.focusNode) {
    return null
  }

  const focusRange = document.createRange()
  focusRange.setStart(sel.focusNode, sel.focusOffset)
  focusRange.collapse(true)

  const rect = focusRange.getBoundingClientRect()
  if (rect.height > 0) {
    return rect
  }

  const inlineRect = inlineEmbedDOMCaretRect(sel.focusNode, sel.focusOffset)
  if (inlineRect) {
    return inlineRect
  }

  // Zero-height rect (empty block, or Firefox edge case) — fall back to the element
  const node = focusRange.startContainer
  const element = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement
  return element ? element.getBoundingClientRect() : null
}

export function SmoothCursorScrollPlugin(): null {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    const rootElement = editor.getRootElement()
    if (!rootElement) {
      return
    }

    const scrollContainer = findScrollContainer(rootElement)
    if (!scrollContainer) {
      return
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) {
        return
      }
      // Only handle plain arrow keys (no modifier combos)
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') {
        return
      }
      if (e.ctrlKey || e.metaKey || e.altKey) {
        return
      }
      // Allow Shift (selection extension) — same scroll logic applies

      // Pre-scroll, before the browser acts on the key.
      //
      // The browser runs its own scroll-into-view as part of the default action,
      // and it only cares about making the caret barely visible — so it fires
      // whenever the caret would land past the edge, and sometimes overshoots by a
      // page. The correction below then pulls back, and the page moves twice for
      // one keystroke (occasionally backwards). Making room for the caret's next
      // line *first* leaves the browser with nothing to do, so the correction is
      // the only scroll that happens.
      const caret = measureCaret()
      let following = false
      if (caret) {
        const containerRect = scrollContainer.getBoundingClientRect()
        const lineHeight = caret.height || 20
        const step = lineHeight * 2 // a line plus room for a block gap
        const distanceToEdge =
          e.key === 'ArrowDown' ? containerRect.bottom - caret.bottom : caret.top - containerRect.top

        if (distanceToEdge < MARGIN + step) {
          following = true
          const maxScroll = scrollContainer.scrollHeight - containerRect.height
          const delta = e.key === 'ArrowDown' ? step : -step
          scrollContainer.scrollTop = Math.max(0, Math.min(scrollContainer.scrollTop + delta, maxScroll))
        }
      }

      requestAnimationFrame(() => {
        const cursorRect = measureCaret()
        if (!cursorRect) {
          return
        }

        const containerRect = scrollContainer.getBoundingClientRect()
        const currentScroll = scrollContainer.scrollTop

        // Cursor position in container's content coordinate space (scroll-independent)
        const cursorTopInContent = cursorRect.top - containerRect.top + currentScroll
        const cursorBottomInContent = cursorRect.bottom - containerRect.top + currentScroll

        // Measure against what is on screen *now*, not against the pre-keypress
        // scroll. The browser applies its own minimal scroll-into-view before this
        // callback runs, so testing the caret's fresh position against the stale
        // viewport made the two disagree: on some presses this handler decided the
        // caret was already comfortable and left the browser's small nudge as the
        // whole movement, on others it applied a full correction on top. The
        // per-keypress distance alternated between a few pixels and ~50, which is
        // the stutter — the page moving unevenly for evenly spaced keystrokes.
        const visibleTop = currentScroll
        const visibleBottom = currentScroll + containerRect.height

        let targetScroll = currentScroll // default: don't scroll

        if (following) {
          // The pre-scroll deliberately overshoots — enough room for whatever the
          // caret's next step turns out to be. Settle it back onto the margin so
          // the page advances by exactly the distance the caret moved and the
          // cursor holds its place on screen. Both writes land in the same frame,
          // so this reads as a single, even step rather than a lurch.
          targetScroll =
            e.key === 'ArrowDown' ? cursorBottomInContent - containerRect.height + MARGIN : cursorTopInContent - MARGIN
        } else if (cursorBottomInContent > visibleBottom - MARGIN) {
          // Cursor is below (or near the bottom of) the visible area
          targetScroll = cursorBottomInContent - containerRect.height + MARGIN
        } else if (cursorTopInContent < visibleTop + MARGIN) {
          // Cursor is above (or near the top of) the visible area
          targetScroll = cursorTopInContent - MARGIN
        }

        // Clamp to valid scroll range
        const maxScroll = scrollContainer.scrollHeight - containerRect.height
        targetScroll = Math.max(0, Math.min(targetScroll, maxScroll))

        scrollContainer.scrollTop = targetScroll
      })
    }

    rootElement.addEventListener('keydown', handleKeyDown)
    return () => {
      rootElement.removeEventListener('keydown', handleKeyDown)
    }
  }, [editor])

  return null
}
